import { t } from "@/i18n-js/instance";
import { useStorageMetricsApi } from "@/react/components/SettingsApp/MediaManager/hooks/useStorageMetricsApi";
import { SkeletonLoader } from "@/react/components/shared/uikit/SkeletonLoader";
import { formatFileSize } from "@/react/helpers/fileSizeHelpers";
import { usePunditUserContext } from "@circle-react/contexts";
import { toLocaleString } from "@circle-react/helpers/number";
import { MinimalProgressBar } from "@circle-react-uikit/MinimalProgressBar";
import { Typography } from "@circle-react-uikit/Typography";

export const ExtraStorageBillingDetails = () => {
  const { currentCommunitySettings } = usePunditUserContext();
  const {
    extraStorage,
    totalBytesUsed,
    totalBytesInPlan,
    isLoadingStorageMetrics,
  } = useStorageMetricsApi();

  if (!currentCommunitySettings?.growth_storage_billing_enabled) {
    return null;
  }

  if (isLoadingStorageMetrics) {
    return <SkeletonLoader variant="card-view" />;
  }

  if (!extraStorage) {
    return null;
  }

  const amount = extraStorage.monthly_price_cents / 100;
  const price = `${extraStorage.currency_symbol}${toLocaleString(amount)}`;

  // Calculate values from bytes
  const totalCapacityBytes =
    totalBytesInPlan + extraStorage.purchased_storage_bytes;
  const usagePercentage =
    totalCapacityBytes > 0 ? (totalBytesUsed / totalCapacityBytes) * 100 : 0;

  return (
    <div className="bg-primary border-primary my-6 rounded-lg border px-8 py-7">
      <div className="border-primary mb-6 flex justify-between border-b pb-6">
        <div className="flex items-center gap-2">
          <Typography.TitleSm weight="bold" data-testid="extra-storage-title">
            {t("settings.billing.extra_storage.title")}
          </Typography.TitleSm>
        </div>
        <Typography.TitleSm weight="bold" data-testid="extra-storage-price">
          {price}
        </Typography.TitleSm>
      </div>

      <div>
        <div className="mb-4">
          <Typography.LabelMd weight="semibold">
            {t("settings.billing.extra_storage.storage_limit")}
          </Typography.LabelMd>
        </div>
        <div className="mb-2">
          <MinimalProgressBar progress={usagePercentage} size="sm" />
        </div>
        <div className="flex justify-between">
          <Typography.LabelSm color="text-light">
            {t("settings.billing.extra_storage.storage_usage", {
              used: formatFileSize(totalBytesUsed),
              total: formatFileSize(totalCapacityBytes),
              addon: formatFileSize(extraStorage.purchased_storage_bytes),
            })}
          </Typography.LabelSm>
        </div>
      </div>
    </div>
  );
};
